import { useCallback, useState } from "react";
import { useLingui } from "@lingui/react";
import { toast } from "sonner";
import { InvoiceStatus } from "@/types/billing";
import { useUpdateInvoiceStatus } from "./useInvoices";

export interface UseCancelAndProceedResult {
  /**
   * Cancel each blocking invoice (canonical cash-in release) then re-run the
   * original contract change. `periods` is used only for the success toast copy.
   */
  run: (
    invoiceIds: string[],
    retryFn: () => Promise<unknown>,
    periods: string[],
  ) => Promise<void>;
  isProcessing: boolean;
}

/**
 * Orchestrates the "Cancel & proceed" flow shared by every contract change path
 * that can collide with an issued invoice (schedule-change, update-contract,
 * update-version-period, cancel-version).
 *
 * It cancels each blocking invoice via the EXISTING invoice status-cancel
 * endpoint — the single canonical cash-in release path
 * (`ReverseInvoiceApplicationsOfficeAwareWithTx`), which releases parent + subsidy
 * splits back to each debtor's credit and preserves the unapplied-credit
 * invariant — and then re-runs the original mutation. No new release logic.
 *
 * Non-atomic by design (two existing endpoints, no backend transaction): if the
 * cancels succeed but `retryFn` throws, the invoices are already cancelled
 * (recoverable — the admin retries or regenerates) and a clear error toast is
 * surfaced before the error re-propagates.
 */
export function useCancelAndProceed(): UseCancelAndProceedResult {
  const { i18n } = useLingui();
  // Depend on the stable `mutateAsync` rather than the whole mutation object
  // (which is a new reference each render) so `run` isn't recreated every render.
  // `silent` suppresses the per-invoice success toast — this flow surfaces a
  // single consolidated toast at the end instead of one per cancelled invoice.
  const { mutateAsync: cancelInvoice } = useUpdateInvoiceStatus({ silent: true });
  const [isProcessing, setIsProcessing] = useState(false);

  const run = useCallback(
    async (
      invoiceIds: string[],
      retryFn: () => Promise<unknown>,
      periods: string[],
    ) => {
      setIsProcessing(true);
      try {
        // 1. Cancel each blocking invoice via the canonical release path.
        for (const id of invoiceIds) {
          await cancelInvoice({
            id,
            status: {
              status: InvoiceStatus.CANCELLED,
              cancellation_reason: i18n._({
                id: "contracts.cancelAndProceed.cancellationReason",
                message:
                  "Cancelled to apply a back-dated contract change or correction.",
              }),
            },
          });
        }

        // 2. Re-run the original change. A failure here is recoverable.
        try {
          await retryFn();
        } catch (retryError) {
          toast.error(
            i18n._({
              id: "contracts.cancelAndProceed.retryFailedV2",
              message:
                "The invoice(s) were cancelled and any paid money is safe as credit, but the change itself didn't go through. Try the change again.",
            }),
          );
          throw retryError;
        }

        // 3. Success — name where the freed money went and frame re-billing as
        // a next step (not homework). Neutral wording: this hook is shared by
        // contract updates, schedule changes, version-period edits and version
        // cancellations, so the copy must not assert a specific operation.
        toast.success(
          i18n._({
            id: "contracts.cancelAndProceed.successNamed",
            message:
              "Done — {count} invoice(s) cancelled. Any money already paid is back as credit on the family's account (and the office's, for the subsidised share). Re-bill {periods} to issue the corrected invoice(s).",
            values: {
              count: invoiceIds.length,
              periods: periods.join(", "),
            },
          }),
        );
      } finally {
        setIsProcessing(false);
      }
    },
    [i18n, cancelInvoice],
  );

  return { run, isProcessing };
}
